import { createFileRoute } from "@tanstack/react-router";
import { Page, SectionEyebrow, CTAButton } from "@/components/SiteLayout";
import balanceren from "@/assets/maya_balanceren.jpeg.asset.json";
import klimmen from "@/assets/sem_klimmen.jpeg.asset.json";
import rollen from "@/assets/maya_rollen.jpeg.asset.json";
import springen from "@/assets/sem_springen.jpeg.asset.json";
import hardlopen from "@/assets/maya_renne.jpeg.asset.json";
import gooien from "@/assets/sem_gooien.jpeg.asset.json";
import trappen from "@/assets/maya_trappen.jpeg.asset.json";
import slingeren from "@/assets/sem_slingeren.jpeg.asset.json";
import glijden from "@/assets/maya_glijden.jpeg.asset.json";
import trekken from "@/assets/sem_trekken.jpeg.asset.json";

export const Route = createFileRoute("/methode")({
  head: () => ({
    meta: [
      { title: "Methode — Hi Coach" },
      { name: "description", content: "Hi Coach werkt met de wetenschappelijke ASM-methode: 10 grondvormen van bewegen voor kinderen van 2 tot 6 jaar." },
      { property: "og:title", content: "De Hi Coach Methode" },
      { property: "og:description", content: "Breed bewegen volgens het Athletic Skills Model." },
      { property: "og:url", content: "/methode" },
    ],
    links: [{ rel: "canonical", href: "/methode" }],
  }),
  component: Methode,
});

const grondvormen = [
  { c: "#FF0000", fg: "white", t: "Balanceren en vallen", d: "Voetje voor voetje over een smalle balk lopen óf veilig leren vallen op een dikke mat. Dit traint het evenwicht.", img: balanceren.url },
  { c: "#FF7F00", fg: "white", t: "Stoeien en vechten", d: "Samen vriendschappelijk stoeien, elkaar proberen uit balans te brengen of tikspelletjes doen. Dit leert kinderen om te gaan met fysiek contact.", img: trekken.url },
  { c: "#FFD700", fg: "#1a1a1a", t: "Gaan en lopen", d: "De basis van alles: netjes wandelen, zijwaarts stappen, huppelen en natuurlijk keihard rennen door een slingerend parcours.", img: hardlopen.url },
  { c: "#00C851", fg: "white", t: "Springen en landen", d: "Met twee voeten tegelijk door gekleurde hoepels springen of als een kikker landen zonder om te vallen.", img: springen.url },
  { c: "#00D4D4", fg: "white", t: "Rollen, duikelen en draaien", d: "Een mooie koprol maken op een zachte mat, zijwaarts rollen als een boomstam of rondjes draaien om je eigen as.", img: rollen.url },
  { c: "#1E90FF", fg: "white", t: "Gooien, vangen, slaan en mikken", d: "Een zachte, felgekleurde bal naar elkaar overgooien, een grote ballon vangen of met een pittenzakje mikken.", img: gooien.url },
  { c: "#4B0082", fg: "white", t: "Trappen, schieten en mikken", d: "Een grote opblaasbal met je voet recht vooruit schoppen in een doel of mikken naar een pion. Dit traint de oog-voetcoördinatie.", img: trappen.url },
  { c: "#8B00FF", fg: "white", t: "Klimmen en klauteren", d: "Via een schuine ladder of een veilig klimrek omhoog klauteren. Dit zorgt voor sterke spieren.", img: klimmen.url },
  { c: "#FF4FA3", fg: "white", t: "Zwaaien en slingeren", d: "Aan de ringen of een touw hangen en lekker heen en weer wiegen. Dit traint het ritme en de timing.", img: slingeren.url },
  { c: "#B30000", fg: "white", t: "Bewegen op – en maken van muziek", d: "Lekker dansen, klappen in de handen, stampen op de maat of het ritme van het voorgelezen verhaal volgen.", img: glijden.url },
] as { c: string; fg: string; t: string; d: string; img?: string }[];

function Methode() {
  return (
    <Page>
      <section className="pt-24 pb-12 bg-white">
        <div className="mx-auto max-w-4xl px-6">
          <SectionEyebrow color="var(--rb-violet)">Methode</SectionEyebrow>
          <h1 className="text-4xl md:text-6xl font-extrabold leading-tight">
            Ontdek hoe wij <span style={{ color: "var(--rb-violet)" }}>bewegen!</span>
          </h1>
          <div className="mt-8 space-y-5 text-lg">
            <p>
              Stel je voor dat jouw kind op zijn 8e voor het eerst een bal moet vangen en dat gewoon niet lukt. Niet omdat het niet slim is, maar omdat de basis nooit is gelegd. Dat is wat er gebeurt als kinderen te vroeg in één specifieke sport worden gestopt, of juist te weinig bewegen in de jaren dat het er echt toe doet.
            </p>
            <p>
              Tussen de 2 en 6 jaar legt een kind de motorische fundering voor de rest van zijn leven. Niet door te voetballen of te turnen, maar door zoveel mogelijk verschillende bewegingen te ervaren. Vallen en opstaan. Klimmen en glijden. Gooien en vangen. Die brede basis is precies waar Hi Coach op is gebouwd.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 gradient-rainbow-soft">
        <div className="mx-auto max-w-4xl px-6">
          <h2 className="text-3xl md:text-4xl font-extrabold">De wetenschap achter de methode</h2>
          <div className="mt-6 space-y-5 text-lg">
            <p>
              Hi Coach werkt volgens het Athletic Skills Model, kortweg ASM. Een wetenschappelijk onderbouwde methode ontwikkeld door bewegingswetenschappers die één ding hebben aangetoond: kinderen die vroeg breed bewegen, presteren later beter in elke sport die ze kiezen.
            </p>
            <p>
              Geen vroege specialisatie dus. Geen "jij bent een voetballer" of "jij gaat turnen." Maar een brede basis van 10 grondvormen die samen de complete motorische ontwikkeling van een kind vormen. Dit is onze doelstelling om aan het eind te belonen met de diploma's.
            </p>
            <p className="font-extrabold">Het mooie is dat kinderen dit niet ervaren als trainen. Ze ervaren het als spelen.</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-3xl md:text-4xl font-extrabold">De 10 Grondvormen in de praktijk</h2>
          <p className="mt-3 text-lg text-muted-foreground max-w-3xl">
            De coaches kijken nauwkeurig hoe het kind groeit in de 10 grondvormen. Let op: de begeleiding en inrichting van het parcours is specifiek afgestemd op de leeftijd: bij de peuters (2-3 jaar) gebeurt dit met actieve ouderhulp, terwijl we bij de kleuters (4-6 jaar) sturen op meer zelfstandigheid.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 gap-6">
            {grondvormen.map((g, i) => (
              <div key={g.t} className="rounded-2xl overflow-hidden shadow-md flex flex-col" style={{ background: g.c, color: g.fg }}>
                {g.img && (
                  <div className="w-full aspect-[16/9] overflow-hidden bg-white/10">
                    <img src={g.img} alt={g.t} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                )}
                <div className="p-6 flex gap-5 items-start">
                  <div className="text-5xl font-extrabold opacity-90 leading-none w-12 shrink-0">{i + 1}</div>
                  <div>
                    <h3 className="text-xl font-extrabold">{g.t}</h3>
                    <p className="mt-2 text-sm/relaxed opacity-95">{g.d}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 gradient-rainbow-soft">
        <div className="mx-auto max-w-4xl px-6">
          <h2 className="text-3xl md:text-4xl font-extrabold">Zo komen deze grondvormen terug in een les</h2>
          <div className="mt-6 space-y-5 text-lg">
            <p>
              Elke les vertaalt de coach de 10 grondvormen naar een prachtig avontuur. Sem en Maya nemen de kinderen mee in een verhaal, en dat verhaal wordt het parcours. Klimmen omdat het personage een berg moet beklimmen. Springen omdat er een rivier over moet. Gooien omdat er een draak verslagen moet worden.
            </p>
            <p>
              De kinderen denken dat ze spelen. En dat klopt ook. Alleen bouwen ze ondertussen aan precies de basis die ze de rest van hun leven meenemen. Aan het einde van de rit krijgt je kind namelijk een écht SEM &amp; MAYA Beweegdiploma waarop alle grondvormen feestelijk staan afgevinkt!
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white text-center">
        <div className="mx-auto max-w-2xl px-6">
          <h2 className="text-3xl md:text-4xl font-extrabold">Wil je zien hoe jouw kind dit beleeft?</h2>
          <div className="mt-8"><CTAButton /></div>
        </div>
      </section>
    </Page>
  );
}
